import { LandingPageContent } from '../types';

const personalizedStoryBooks: LandingPageContent = {
	path: '/personalized-story-books',
	seoTitle: "Personalized Story Books — Your Child as the Hero",
	metaDescription:
		'Custom personalized story books starring your child. Upload one photo, choose an art style, approve the illustrations, and we print and ship a one-of-a-kind hardcover keepsake.',
	heading: 'Personalized Story Books',
	subheading: 'One photo in. A printed adventure starring your child out.',
	heroImage: '/assets/hero/kid-3/classic_scene.png',
	intro: `A personalized story book is a children's book in which your child *is* the main character — not as a name inserted into a template, but as an illustrated character drawn consistently across every page.

We build ours from a single photo. You choose the art style and the story, we generate the illustrations, you approve them, and we print and ship the finished book.`,
	sections: [
		{
			heading: 'How it works',
			body: `1. **Pick a story.** Each story has a fixed narrative arc — a voyage, a rescue, a night-time adventure — with room for your child at the centre of it.
2. **Upload a photo.** One clear, front-facing photo is enough. No professional photography required.
3. **Choose an art style.** Watercolour, classic storybook, flat modern illustration, comic, or a Pixar-like 3D render.
4. **Approve the artwork.** We generate the character, then every page. You review the whole book and can request a page be redone before anything is printed.
5. **We print and ship.** Properly printed and bound, delivered to your door.`,
		},
		{
			heading: 'Choosing an art style',
			body: `**Watercolour** is soft and traditional. It photographs well and suits gifts intended as keepsakes — it looks like a book you'd find in a good bookshop.

**Classic storybook** is the closest to the illustrated children's books most adults remember. Warm, detailed, slightly old-fashioned in a deliberate way.

**Flat modern** is bold and graphic, with strong colour blocking. It reads well for younger children because shapes are simple and high-contrast.

**Comic** suits older children — panelled, energetic, more action than atmosphere.

**Pixar-style 3D** is the one most children pick when given the choice. It's rendered and glossy, and the likeness tends to be the most immediately recognisable.

If you're buying for a child under four, favour flat modern or Pixar. Both prioritise clear, readable shapes.`,
		},
		{
			heading: 'What makes the likeness work',
			body: `The hard part of a personalized book is not putting a child into one illustration — it's keeping them recognisably the *same* child across twelve pages, at different angles, in different lighting, wearing different things.

We generate a character reference first, then illustrate every scene against it. That's why the approval step exists: you see the character before the book is built around them, and you see every page before it's printed.

For the best result, upload a photo where the face is well-lit, roughly front-on, and not obscured by hats, sunglasses or hands.`,
		},
		{
			heading: "Who they're for",
			body: `Personalized story books work best between roughly ages two and eight.

Below two, the appeal is recognition rather than narrative — still worthwhile, but choose a simpler story. Above eight, most children start to feel that picture books are for younger kids, though the keepsake value for parents remains.

They're a common choice for birthdays, Christmas, new siblings, adoption days, and first days of school — any moment where the point is to tell a child something about themselves.`,
		},
		{
			heading: 'Languages',
			body: `Stories can be produced in several languages, including Greek and Arabic. For right-to-left languages the entire layout is mirrored — page order, text alignment and illustration placement — rather than simply translating the words into a left-to-right template.

This matters more than it sounds. A right-to-left book with left-to-right pagination reads backwards.`,
		},
	],
	picks: [
		{
			label: 'Browse storybooks',
			href: '/products',
			description: 'See the available stories and art styles.',
		},
		{
			label: 'Personalized coloring books',
			href: '/personalized-coloring-books',
			description: 'The same character, as outline pages to colour in.',
		},
	],
	faqs: [
		{
			question: 'How long does a personalized story book take to arrive?',
			answer:
				'Artwork is generated within minutes of ordering, but the book is not printed until you have reviewed and approved the pages. After approval, printing and shipping times depend on your delivery address. Allow two to three weeks overall to be comfortable.',
		},
		{
			question: 'How many pages is a personalized story book?',
			answer:
				'Our standard personalized storybook is twelve illustrated pages plus a cover, printed in landscape format.',
		},
		{
			question: 'What kind of photo works best?',
			answer:
				'A clear, well-lit, roughly front-facing photo where the whole face is visible. Avoid sunglasses, hats that shadow the face, extreme angles, and heavily filtered images. A normal phone photo taken near a window is ideal.',
		},
		{
			question: 'Can I change the illustrations if I do not like them?',
			answer:
				'Yes. You approve the book page by page before it is printed, and individual pages can be regenerated if they are not right.',
		},
		{
			question: 'Can two children appear in the same book?',
			answer:
				'Our standard stories are built around a single hero character. For siblings, most customers order a book each so that both children get to be the main character.',
		},
		{
			question: 'What happens to the photo I upload?',
			answer:
				'Uploaded photos are used to generate the illustrated character and are held only as long as needed to fulfil the order. Temporary uploads are automatically deleted, and you can request deletion of your data at any time from your account settings.',
		},
	],
	related: [
		{ label: 'Gifts for kids', href: '/gifts-for-kids' },
		{ label: 'Personalized Greek books', href: '/personalized-greek-books-for-kids' },
		{ label: 'Personalized Arabic books', href: '/personalized-arabic-books-for-kids' },
		{
			label: 'How to make a personalized storybook',
			href: '/blog/how-to-make-a-personalized-storybook',
		},
		{
			label: 'Why personalized books help kids read',
			href: '/blog/why-personalized-books-help-kids-read',
		},
	],
	cta: { label: 'Create a personalized book', href: '/products' },
	breadcrumbParents: [],
};

export default personalizedStoryBooks;
